import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { User, OrganizationRole } from '../../models/user.model';
import { Role, RolePermission } from '../../models/role.model';
import { NavItem, NavigationSection } from '../interfaces/navigation.interface';
import { AuthService } from '../auth/services/auth.service';

export interface MenuItem {
    label: string;
    icon: string;
    route: string;
    children?: MenuItem[];
}

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private readonly defaultNavigation: NavigationSection[] = [
    // {
    //   items: [
    //     { label: 'Dashboard', icon: 'bi-grid', route: '/dashboard' }
    //   ]
    // },
    {
      items: [
        { label: 'Programs', icon: 'bi-folder', route: '/programs', featureId: 100 },
        { label: 'Adioscope', icon: 'bi-file-earmark-text', route: '/adioscope', featureId: 100 },
        // { label: 'My Life', icon: 'bi-diagram-2', route: '/life', featureId: 100, badge: 'Beta' },
        { label: 'Vision Planner', icon: 'bi-person-workspace', route: '/vision-planner', featureId: 100 },
        { label: 'Pursuits', icon: 'bi-palette', route: '/pursuits', featureId: 100 },
        {
          label: 'Mind Fitness Report',
          icon: 'bi-heart-pulse',
          route: '/mind-fitness/report',
          featureId: 100,
          badge: 'Beta'
        },
        { label: 'Library', icon: 'bi-book', route: '/library', featureId: 100 },
        { label: 'Audiobooks / Downloads', icon: 'bi-shop', route: '/store', featureId: 100 },
        // { label: 'My Coaches', icon: 'bi-person-heart', route: '/coaches', featureId: 100 },
        { label: 'Support', icon: 'bi-headset', route: '/support', featureId: 100 },
      ]
    },
    // {
    //   label: 'Settings',
    //   items: [
    //     { label: 'Profile', icon: 'bi-person', route: '/profile', featureId: 100 },
    //   ]
    // }
  ];

  private adminNavigation: NavigationSection[] = [
    {
      label: 'Manage',
      items: [
        { label: 'Stakeholders', icon: 'bi-people', route: '/manage/stakeholders', featureId: 100 },
        { label: 'Content', icon: 'bi-file-earmark-text', route: '/manage/content', featureId: 100 },
        // { label: 'Users', icon: 'bi-people', route: '/manage/users', featureId: 100 },
        { label: 'Recordings', icon: 'bi-play', route: '/manage/recordings', featureId: 100 },
        { label: 'Conversations', icon: 'bi-chat-dots', route: '/manage/conversations', featureId: 100 },
        { label: 'Coaching', icon: 'bi-person-heart', route: '/manage/coaching', featureId: 100 },
        { label: 'Store', icon: 'bi-shop', route: '/manage/store', featureId: 100 },
        { label: 'Support Admin', icon: 'bi-headset', route: '/manage/support/admin', featureId: 100 },
        { label: 'Service Tokens', icon: 'bi-key', route: '/manage/service-tokens', featureId: 100 },
        { label: 'Events Logs', icon: 'bi-envelope', route: '/manage/events', featureId: 100 }
      ]
    }
  ];

  private coachNavigation: NavigationSection[] = [
    {
      items: [
        { label: 'My Students', icon: 'bi-people', route: '/coach-console', featureId: 100 },
        { label: 'Coach Inbox', icon: 'bi-chat-dots', route: '/coach-console/inbox', featureId: 100 }
      ]
    }
  ];

  private partnerNavigation: NavigationSection[] = [
    {
      // label: 'Manage Partnerships',
      items: [
        { label: 'Manage Center', icon: 'bi-building', route: '/partner-console', featureId: 100 }
      ]
    }
  ];

  private supportAgentNavigation: NavigationSection[] = [
    {
      label: 'Support Agent',
      items: [
        { label: 'Agent Dashboard', icon: 'bi-headset', route: '/manage/support/agent', featureId: 100 },
        // { label: 'Ticket Management', icon: 'bi-ticket-detailed', route: '/support/agent/tickets', featureId: 100 }
      ]
    }
  ];

  private supportLeadNavigation: NavigationSection[] = [
    {
      label: 'Support Lead',
      items: [
        { label: 'Lead Dashboard', icon: 'bi-people', route: '/manage/support/lead/dashboard', featureId: 100 },
        { label: 'Team Management', icon: 'bi-person-gear', route: '/manage/support/lead/team', featureId: 100 }
      ]
    }
  ];

  private communicationNavigation: NavigationSection[] = [
    {
      label: 'Manage Notifications',
      items: [
        { label: 'Notifications', icon: 'bi-bell', route: '/manage/notifications', featureId: 100 },
      ]
    }
  ];

  private menuSubject = new BehaviorSubject<MenuItem[]>([]);
  menu$ = this.menuSubject.asObservable();

  navigationSubject = new BehaviorSubject<NavigationSection[]>([]);
  navigation$ = this.navigationSubject.asObservable();

  constructor(private authService: AuthService) {
    this.authService.tokenData$.subscribe((tokenData: any) => {
      console.log(tokenData);
      const navigation: NavigationSection[] = [...this.defaultNavigation];
      if (tokenData) {
        if (tokenData.roles && tokenData.roles.includes("TRAINER")) {
          navigation.push(...this.coachNavigation);
        }
        if (tokenData.roles && tokenData.roles.includes("PARTNER")) {
          navigation.push(...this.partnerNavigation);
        }
        if (tokenData.roles && tokenData.roles.includes("SUPPORT_AGENT")) {
          navigation.push(...this.supportAgentNavigation);
        }
        // if (tokenData.roles && tokenData.roles.includes("SUPPORT_LEAD")) {
        //   navigation.push(...this.supportLeadNavigation);
        // }
        if (tokenData.roles && (tokenData.roles.includes("COMMUNICATION") || tokenData.roles.includes("ADMIN"))) {
          navigation.push(...this.communicationNavigation);
        }
        if (tokenData.roles && tokenData.roles.includes("ADMIN")) {
          navigation.push(...this.adminNavigation);
        } 
      }
      this.navigationSubject.next(navigation);
    });
  }

  private filterNavItemsByRole(items: NavItem[], role: Role): NavItem[] {
    return items.filter(item => {
      if (!item.featureId) return true;
      return role.permissions.map(permission => permission.featureId).includes(item.featureId);
    }).map(item => ({
      ...item,
      children: item.children ? this.filterNavItemsByRole(item.children, role) : undefined
    }));
  }

  private filterNavigationByRole(navigation: NavigationSection[], role: Role): NavigationSection[] {
    return navigation.map(section => ({
      ...section,
      items: this.filterNavItemsByRole(section.items, role)
    })).filter(section => section.items.length > 0);
  }

  updateNavigation(user: User, currentOrganizationId: string): void {
    // const organizationRole = user.organizationRoles.find(
    //   role => role.organizationId === currentOrganizationId
    // );

    // if (!organizationRole) {
    //   this.navigationSubject.next([]);
    //   return;
    // }

    // const filteredNavigation = this.filterNavigationByRole(
    //   this.defaultNavigation,
    //   organizationRole.role
    // );

    // this.navigationSubject.next(filteredNavigation);
  }

  getNavigation(): Observable<NavigationSection[]> {
    return this.navigationSubject.asObservable();
  }

  getFilteredNavigation(role: Role): Observable<NavigationSection[]> {
    return this.getNavigation().pipe(
      map(navigation => this.filterNavigationByRole(navigation, role))
    );
  }

  reset(): void {
    this.menuSubject.next([]);
    this.navigationSubject.next([]);
  }
} 